import React, {useState} from 'react';
import {Cell, Label, Pie, PieChart, Tooltip} from 'recharts';
import {ChartModel} from "../models/ChartDataModel";
import {Localizer} from "../../../infrastructure/localization/localizer";
import useScreenResolution from "../../../core/hooks/useScreenResolution";
import classNames from "classnames";
import {CustomLabel} from "./customDashboardComponents/customPieLabel";
import {CustomLegend} from "./customDashboardComponents/customLegend";
import {LegendPlacement} from "../models/LegendPlacement";
import CustomTooltipContent from "../../../core/components/tooltips/CustomTooltipContent";
import {CustomActiveShape} from "./customDashboardComponents/customActiveShape";

type MultiLevelPieChartProps = {
    datasets: ChartModel[];
    labelText?: string;
    diagramTitle?: string;
    width?: number;
    height?: number;
    showLabels?: boolean;
    showLegendLabels?: boolean;
    showCenterLabel?: boolean;
    fullLegendWidth?: boolean;
    totalAmount: number;
    overrideInnerRadius?: number;
    legendPlacement?: LegendPlacement;
}

export const radiusIncrement = 22;

const MultiLevelPieChart = ({
                                datasets,
                                width = 500,
                                height = 350,
                                showLabels = false,
                                labelText,
                                totalAmount,
                                overrideInnerRadius,
                                legendPlacement = LegendPlacement.Right,
                                showLegendLabels = true,
                                diagramTitle,
                                showCenterLabel = false,
                                fullLegendWidth = false,
                            }: MultiLevelPieChartProps) => {
    const initialInnerRadius = overrideInnerRadius ?? 100;
    const initialOuterRadius = datasets.length > 1 ? 120 : 120 + radiusIncrement;
    const {isMobile} = useScreenResolution();
    width = isMobile ? 300 : width;
    const [activeElementId, setActiveElementId] = useState<string | undefined>(undefined);
    const [activeDatasetName, setActiveDatasetName] = useState<string>();
    const [activeDatasetIndex, setActiveDatasetIndex] = useState<number>(-1);

    const onPieEnter = (elementId: string, datasetName: string, datasetIndex: number) => {
        setActiveElementId(elementId)
        setActiveDatasetName(datasetName);
        setActiveDatasetIndex(datasetIndex);
    };

    function isLastIndexInDatasets(entry: ChartModel, index: number) {
        return activeDatasetIndex !== -1 && activeDatasetName === entry.name && index === datasets.length - 1;
    }

    const CustomTooltip = () => {
        const activeElement = datasets[activeDatasetIndex]?.elements.find(x => x.id === activeElementId);
        return (
            <CustomTooltipContent>
                {activeElement?.parentChartName && activeDatasetIndex > 0 && activeElement?.parentChartName !== activeElement?.name &&
                    <CustomTooltipContent.Item
                        key={activeElementId + activeElement?.parentChartName}
                        index={datasets[activeDatasetIndex]?.elements.indexOf(activeElement)}
                        color={activeElement?.parentChartFill ?? ""}
                        text={`${activeElement?.parentChartName}`}
                    />
                }
                {activeElement && <CustomTooltipContent.Item
                    key={activeElementId + activeElement?.name}
                    index={datasets[activeDatasetIndex]?.elements.indexOf(activeElement)}
                    color={activeElement?.fill}
                    text={`${activeElement?.name} (${activeElement?.amount})`}
                    subtleText={`${Localizer.dashboard_part()}: ${(activeElement?.amount / totalAmount * 100).toFixed(0)}%`}
                />}
            </CustomTooltipContent>
        );
    };

    return (
        <div
            className={classNames("wrap", legendPlacement === LegendPlacement.Right ? "flex-container-row align-items-center" : "flex-container-column", isMobile && "justify-content-center")}>
            <div className={classNames("flex-container-column wrap align-items-center flex-grow")}>
                {diagramTitle &&
                    <h5 className={"margin-top-m"}>{diagramTitle}</h5>
                }
                <PieChart height={height} width={width}>
                    {datasets?.map((entry, datasetIndex) => (
                        <Pie
                            key={entry.name + datasetIndex}
                            labelLine={datasetIndex === datasets.length - 1}
                            label={datasetIndex === datasets.length - 1 && !activeElementId && showLabels}
                            data={entry?.elements}
                            dataKey={"amount"}
                            cx="50%"
                            cy="50%"
                            innerRadius={initialInnerRadius + (datasetIndex * radiusIncrement)}
                            outerRadius={initialOuterRadius + (datasetIndex * radiusIncrement)}
                            activeIndex={entry.elements.findIndex(x => x.id === activeElementId)}
                            animationDuration={300}
                            onMouseLeave={() => setActiveElementId(undefined)}
                            activeShape={!!activeElementId && activeDatasetName === entry.name
                                ? <CustomActiveShape
                                    showCursorPointer={!!entry?.elements.find(x => x.id === activeElementId)?.handleOnClick}
                                    customOuterRadius={initialOuterRadius + ((datasets.length - 1) * radiusIncrement)}
                                    maxOuterRadius={initialOuterRadius + ((datasets.length - 1) * radiusIncrement)}
                                    customInnerRadius={initialInnerRadius + (datasetIndex * radiusIncrement)}
                                    elementName={entry?.elements.find(x => x.id === activeElementId)?.name}
                                    isLastIndex={isLastIndexInDatasets(entry, datasetIndex)}
                                    index={datasetIndex}
                                    datasetAmount={datasets.length - 1}
                                    showInnerTekst={false}
                                    onClickCallback={entry?.elements.find(x => x.id === activeElementId)?.handleOnClick}
                                />
                                : undefined
                            }
                            style={{outline: 'none'}}>
                            {datasetIndex === 0 && showCenterLabel && //Add label to the center, at first index.
                                <Label content={
                                    <CustomLabel
                                        labelText={labelText}
                                        value={totalAmount}
                                        viewBox={{cx: 0, cy: 0}}/>}
                                        position="center"
                                />
                            }
                            {entry.elements?.map((element, index) => (
                                <Cell key={`cell-${index}`} stroke={datasets.length > 1 ? "#FFFFFF" : "none"}
                                      onClick={element.handleOnClick}
                                      onMouseEnter={() => onPieEnter(element.id, entry.name, datasetIndex)}
                                      onMouseLeave={() => setActiveElementId(undefined)}
                                />
                            ))}
                        </Pie>
                    ))}
                    <Tooltip
                        wrapperStyle={{
                            visibility: (!!activeElementId && activeDatasetName === datasets[activeDatasetIndex]?.name) ? "visible" : "hidden",
                            zIndex: 9999
                        }} //used to activate when hovering legend
                        content={<CustomTooltip/>}
                    />
                </PieChart>
            </div>

            <div
                className={classNames("align-self-center flex-container-column margin-m flex-fill", legendPlacement === LegendPlacement.Right && "padding-left-m", legendPlacement === LegendPlacement.Bottom && "")}
                style={{maxHeight: height, overflowY: "auto"}}>
                {datasets.map((entry, datasetIndex) => (
                    <>
                        {showLegendLabels &&
                            <h5 className={"margin-bottom-s"}>{entry.name}</h5>
                        }
                        <div
                            className={classNames("wrap", legendPlacement === LegendPlacement.Bottom ? "flex-container-row" : "flex-container-column", !isMobile && !fullLegendWidth && "justify-content-center")}
                            style={{width: legendPlacement === LegendPlacement.Bottom && !fullLegendWidth ? width : ""}}
                        >
                            {entry?.elements
                                .slice() // Create a shallow copy to avoid mutating the original array
                                .sortBy(x => x.name)
                                .map((element, elementIndex) => (
                                <CustomLegend key={`${element.name + elementIndex + datasetIndex}`}
                                              onMouseEnter={() => onPieEnter(element.id, entry.name, datasetIndex)}
                                              onMouseLeave={() => setActiveElementId(undefined)}
                                              text={`${element?.name}`}
                                              amount={element.amount}
                                              subText={element?.subName ? `- ${element?.subName}` : undefined}
                                              additionalClasses={fullLegendWidth ? "w-300" : ""}
                                              color={element.fill}
                                              onClickCallback={element.handleOnClick}
                                              active={activeElementId === element.id && activeDatasetName === entry.name}
                                />
                            ))}
                        </div>
                    </>
                ))
                }
            </div>
        </div>
    );
}

export default MultiLevelPieChart;
